import { useCallback, useEffect, useMemo } from 'react';
import { GlyphSeries, LineSeries } from '@visx/xychart';

import { useChartContext } from '../../../components/ChartContext';
import { DIM_OPACITY_FACTOR } from '../../../constants';
import type { ChartDataPoint, ChartDataPointValue } from '../../../types/ChartDataPoint';
import { remapYValueIfRight } from '../../../utils/scaleHelpers';
import type { SeriesProps } from '../../types/SeriesProps';
import { useTooltipContext } from '../Tooltip/TooltipContext';

import styles from './Series.module.css';

/**
 * Series component for composable LineChart.
 *
 * Wrapper around visx's LineSeries (and optionally GlyphSeries for markers).
 * Handles data transformation and auto-coloring based on series order.
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <Series
 *     seriesKey="latency"
 *     name="API Latency"
 *     data={data}
 *     dataKey="latency"
 *     color="#4d77ff"
 *     showMarkers
 *   />
 * </LineChart>
 * ```
 */
export function Series({
  seriesKey,
  name,
  dataKey,
  color,
  showMarkers = false,
  showGlyphs = true,
  lineStyle = 'solid',
  strokeWidth = 2,
  stroke,
  xAccessor,
  yAccessor,
  tooltipRowIcon,
  showInTooltip = true,
  dimmed = false,
  yAxis = 'left',
  ...rest
}: SeriesProps) {
  const {
    visibleData,
    xDataKey,
    xScaleType,
    colorScale,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
    yScaleConfig,
    yScaleRightConfig,
  } = useChartContext();
  const { activeXValue } = useTooltipContext();

  // Determine color (use provided or auto-assign from ordinal scale)
  // The scale automatically assigns colors as new series keys are encountered
  const seriesColor = color || colorScale(seriesKey);

  // Register series metadata for tooltip display
  // Only register if showInTooltip is true (default behavior)
  useEffect(() => {
    if (!showInTooltip) {
      return;
    }

    registerSeriesDisplayMetadata(seriesKey, {
      name,
      color: seriesColor,
      dataKey,
      tooltipRowIcon,
      yAxis,
    });

    return () => {
      unregisterSeriesDisplayMetadata(seriesKey);
    };
  }, [
    showInTooltip,
    seriesKey,
    name,
    seriesColor,
    dataKey,
    tooltipRowIcon,
    yAxis,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
  ]);

  // Helper to access x-value from data point using either string or symbol key
  const getXValue = useCallback(
    (d: ChartDataPoint | undefined): ChartDataPointValue => {
      if (!d) {
        return null;
      }
      return d[xDataKey] ?? null;
    },
    [xDataKey],
  );

  // Build accessors (use provided or build defaults)
  // Note: visx may pass undefined in some edge cases (e.g., when computing nearest datum across series with different x-values)
  // We provide defensive checks to prevent crashes
  const defaultXAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      const value = getXValue(d);
      if (value === null || value === undefined) {
        // For band scales, return empty string; for continuous scales, return 0
        return xScaleType === 'band' ? '' : 0;
      }
      // For band scales, convert numeric values to strings to match the scale domain
      // Band scale domains are always string[] (e.g., ['1', '2', '3'])
      return xScaleType === 'band' ? String(value) : value;
    },
    [getXValue, xScaleType],
  );
  const defaultYAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      if (!d) {
        return null;
      }
      const value = d[dataKey];
      // Return null for null/undefined values so visx skips those points
      return value ?? null;
    },
    [dataKey],
  );

  const resolvedYAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      const raw = (yAccessor ?? defaultYAccessor)(d);
      if (typeof raw !== 'number') {
        return raw;
      }
      return remapYValueIfRight(raw, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain);
    },
    [yAccessor, defaultYAccessor, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain],
  );

  // Filter visibleData to only include rows where this series has a non-null value.
  // Used for glyphs (markers) and single-point detection — we don't want markers at gap points.
  const filteredData = useMemo(() => {
    return visibleData.filter((datum) => {
      const value = datum[dataKey];
      return value !== null && value !== undefined;
    });
  }, [visibleData, dataKey]);

  // For single data points, increase stroke width to make them visible
  // SVG paths with zero length are invisible unless strokeLinecap="round" renders a circle
  const isSinglePoint = filteredData.length === 1;
  const effectiveStrokeWidth = isSinglePoint ? Math.max(6, strokeWidth) : strokeWidth;

  // Isolated points: non-null data values where both neighbors in visibleData are null.
  // These form zero-length path segments in LineSeries (invisible without strokeLinecap="round"),
  // so we force their GlyphSeries markers to always be visible.
  const isolatedXValues = useMemo(() => {
    const isolated = new Set<ChartDataPointValue>();
    for (let i = 0; i < visibleData.length; i++) {
      const datum = visibleData[i];
      const value = datum[dataKey];
      if (value === null || value === undefined) {
        continue;
      }
      const prevVal = i > 0 ? visibleData[i - 1][dataKey] : null;
      const nextVal = i < visibleData.length - 1 ? visibleData[i + 1][dataKey] : null;
      const prevIsNull = prevVal === null || prevVal === undefined;
      const nextIsNull = nextVal === null || nextVal === undefined;
      const xVal = datum[xDataKey];
      if (prevIsNull && nextIsNull && xVal !== undefined) {
        isolated.add(xVal);
      }
    }
    return isolated;
  }, [visibleData, dataKey, xDataKey]);

  return (
    <>
      {/* Line — pass all visible data (including null-valued gap points) so visx's internal
          isDefined check can break the path at gaps rather than drawing through them. */}
      <LineSeries
        dataKey={seriesKey}
        data={visibleData}
        xAccessor={xAccessor ?? defaultXAccessor}
        yAccessor={resolvedYAccessor}
        stroke={stroke ?? seriesColor}
        strokeWidth={effectiveStrokeWidth}
        strokeOpacity={dimmed ? DIM_OPACITY_FACTOR : 1}
        strokeLinecap={isSinglePoint ? 'round' : undefined}
        strokeDasharray={lineStyle === 'dashed' ? '5,5' : lineStyle === 'dotted' ? '2,3' : undefined}
        {...rest}
      />

      {/* Markers - always render for active point, show all if showMarkers={true} */}
      {showGlyphs && (
        <GlyphSeries
          dataKey={`${seriesKey}-glyphs`}
          data={filteredData}
          xAccessor={xAccessor ?? defaultXAccessor}
          yAccessor={resolvedYAccessor}
          renderGlyph={(glyphProps) => {
            // Check if this marker is active (tooltip is showing for this X value)
            const datum = glyphProps.datum as ChartDataPoint | undefined;
            const xValue = getXValue(datum);
            const isActive = activeXValue !== null && xValue === activeXValue;

            // Isolated points have no visible line segment; always show their glyph as a dot.
            const isIsolated = isolatedXValues.has(xValue);
            // When showMarkers is false, render with opacity/scale transition for active state
            const shouldHide = !showMarkers && !isActive && !isIsolated;

            const isSolid = isActive || isIsolated;
            return (
              <circle
                cx={glyphProps.x}
                cy={glyphProps.y}
                r={4}
                fill={isSolid ? seriesColor : 'var(--lp-color-bg-ui-primary)'}
                stroke={isSolid ? 'var(--lp-color-bg-ui-primary)' : seriesColor}
                strokeWidth={2}
                className={`${styles.marker} ${shouldHide ? styles.markerInactive : styles.markerActive}`}
                style={{
                  // Dim visible markers (e.g. isolated-point dots) with the line when the
                  // series is de-emphasized. Leave hidden markers to the CSS class (opacity 0).
                  opacity: dimmed && !shouldHide ? DIM_OPACITY_FACTOR : undefined,
                  pointerEvents: shouldHide ? 'none' : 'auto',
                  transformOrigin: `${glyphProps.x}px ${glyphProps.y}px`,
                }}
              />
            );
          }}
        />
      )}
    </>
  );
}

// displayName for identifying Series in children extraction (supports React.memo)
Series.displayName = 'Series';
